import { FormEvent, useState } from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { AccountShell } from "@/components/AccountShell"
import { PageHeader } from "@/components/PageHeader"
import { LicenseStatusBadge } from "@/components/LicenseStatusBadge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { CopyField } from "@/components/ui/copy-field"
import { SubNav } from "@/components/ui/sub-nav"
import type { PageProps } from "@/types/inertia"

interface SeatRow {
  id: number
  source: "subscription" | "lifetime"
  assigned_to: { id: number; email: string; role: string } | null
  license: {
    id: number
    key: string
    status: string
    device_count: number
    device_limit: number
  } | null
}

interface MemberRow {
  id: number
  email: string
  role: string
  has_seat: boolean
}

interface InvitationRow {
  id: number
  email: string
  sent_at: string
}

type SeatsProps = PageProps<{
  seats: SeatRow[]
  members: MemberRow[]
  invitations: InvitationRow[]
  seat_summary: { total: number; assigned: number }
}>

const TABS = [
  { key: "seats", label: "Seats" },
  { key: "members", label: "Members" },
]

export default function SeatsIndex() {
  const { props } = usePage<SeatsProps>()
  const { seats, members, invitations, seat_summary: summary } = props
  const [tab, setTab] = useState("seats")

  return (
    <>
      <Head title="Seats & Team">
        <meta
          name="description"
          content="Assign Flywrite seats, view license keys, and manage your team."
        />
        <meta property="og:title" content="Seats & Team" />
        <meta
          property="og:description"
          content="Assign Flywrite seats, view license keys, and manage your team."
        />
      </Head>
      <AccountShell>
        <PageHeader
          title="Seats & Team"
          description={`${summary.assigned} of ${summary.total} seats assigned. Each seat is a license key for one person.`}
          actions={
            <>
              <InviteDialog />
              <Link href="/account/billing" className="btn btn-secondary btn-small">
                Add seats
              </Link>
            </>
          }
          tabs={<SubNav items={TABS} active={tab} onChange={setTab} ariaLabel="Seats & Team sections" />}
        />

        {tab === "seats" ? (
          <section className="mt-8">
            <div className="space-y-4">
              {seats.length === 0 && (
                <p className="text-sm text-ink-muted">
                  No seats yet. <Link href="/buy">Purchase Flywrite</Link> to get started.
                </p>
              )}
              {seats.map((seat) => (
                <SeatCard key={seat.id} seat={seat} members={members} />
              ))}
            </div>
          </section>
        ) : (
          <>
            <section className="mt-8">
              <ul className="divide-y divide-hairline rounded-lg border border-hairline">
                {members.map((member) => (
                  <li
                    key={member.id}
                    className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="text-sm text-ink-body">{member.email}</p>
                      <p className="text-xs text-ink-muted">
                        {member.role === "owner" ? "Owner" : "Member"}
                        {member.has_seat ? " · has a seat" : " · no seat"}
                      </p>
                    </div>
                    {member.role !== "owner" && <RemoveMemberButton member={member} />}
                  </li>
                ))}
              </ul>
            </section>

            {invitations.length > 0 && (
              <section className="mt-10">
                <h2>Pending invitations</h2>
                <ul className="mt-4 divide-y divide-hairline rounded-lg border border-hairline">
                  {invitations.map((invitation) => (
                    <li
                      key={invitation.id}
                      className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <p className="text-sm text-ink-body">{invitation.email}</p>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            router.post(`/account/invitations/${invitation.id}/resend`, {}, { preserveScroll: true })
                          }
                        >
                          Resend
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            router.delete(`/account/invitations/${invitation.id}`, { preserveScroll: true })
                          }
                        >
                          Revoke
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </AccountShell>
    </>
  )
}

function SeatCard({ seat, members }: { seat: SeatRow; members: MemberRow[] }) {
  const [assignee, setAssignee] = useState("")
  const available = members.filter((m) => !m.has_seat)

  return (
    <div className="callout">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          {seat.license && <LicenseStatusBadge status={seat.license.status} />}
          <span className="text-sm text-ink-muted">
            {seat.source === "lifetime" ? "Lifetime seat" : "Subscription seat"}
            {seat.license &&
              ` · ${seat.license.device_count}/${seat.license.device_limit} devices`}
          </span>
        </div>

        {seat.assigned_to ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-ink-body">{seat.assigned_to.email}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                router.post(`/account/seats/${seat.id}/unassign`, {}, { preserveScroll: true })
              }
            >
              Unassign
            </Button>
          </div>
        ) : available.length > 0 ? (
          <div className="flex items-center gap-2">
            <Select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              className="max-w-52"
              aria-label="Assign seat to member"
            >
              <option value="">Assign to…</option>
              {available.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.email}
                </option>
              ))}
            </Select>
            <Button
              variant="secondary"
              size="sm"
              disabled={!assignee}
              onClick={() =>
                router.post(
                  `/account/seats/${seat.id}/assign`,
                  { account_user_id: assignee },
                  { preserveScroll: true },
                )
              }
            >
              Assign
            </Button>
          </div>
        ) : (
          <span className="text-sm text-ink-muted">Unassigned — invite someone first</span>
        )}
      </div>

      {seat.license && (
        <div className="mt-3">
          <CopyField value={seat.license.key} ariaLabel="Copy license key" />
        </div>
      )}
    </div>
  )
}

function InviteDialog() {
  const [open, setOpen] = useState(false)
  const form = useForm({ email: "" })
  const { props } = usePage<PageProps>()

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post("/account/invitations", {
      preserveScroll: true,
      onSuccess: () => {
        form.reset()
        setOpen(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">Invite member</Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Invite a team member</DialogTitle>
          <DialogDescription>
            They'll get an email invitation. Once they join, assign them a seat to issue their
            license.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="invite-email">Email</label>
            <Input
              id="invite-email"
              type="email"
              required
              value={form.data.email}
              aria-invalid={!!props.errors?.email}
              onChange={(e) => form.setData("email", e.target.value)}
            />
            {props.errors?.email && (
              <p className="text-xs text-danger-display">{props.errors.email}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={form.processing}>
              Send invitation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function RemoveMemberButton({ member }: { member: MemberRow }) {
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          Remove
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Remove {member.email}?</DialogTitle>
          <DialogDescription>
            {member.has_seat
              ? "Their seat returns to the pool and their devices will stop validating."
              : "They'll lose access to this account."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              router.delete(`/account/members/${member.id}`, {
                preserveScroll: true,
                onSuccess: () => setOpen(false),
              })
            }
          >
            Remove member
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
